import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import { Box, Typography, Button, ButtonGroup } from "@mui/material";
import { Chart, registerables } from "chart.js";
import { getPerformanceHistory } from "services/api";
import PropTypes from "prop-types";
import LoadingScreen from "components/Loading";
import MDButton from "components/MDButton";
import { useTranslation } from "react-i18next";

Chart.register(...registerables);

const transformKpiHistory = (data) => {
  return data.map(([averageResult, month, year]) => ({
    year,
    month,
    averageResult: averageResult !== null ? averageResult : null, // Ensure null stays null
  }));
};

const WeighedHistoryChart = ({ employee, isMe }) => {
  const { t } = useTranslation();
  const [historyData, setHistoryData] = useState();
  const [selectedView, setSelectedView] = useState("thisYear");
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const data = await getPerformanceHistory(employee.id);
      if (data !== "error") {
        setHistoryData(transformKpiHistory(data));
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (historyData) {
      const filteredData = filterPerformanceData(selectedView);
      setChartData(formatChartData(filteredData));
    }
  }, [selectedView, historyData]);

  const filterPerformanceData = (range) => {
    const currentYear = new Date().getFullYear();

    let pastYearLimit;
    switch (range) {
      case "thisYear":
        pastYearLimit = currentYear;
        break;
      case "lastYear":
        pastYearLimit = currentYear - 1;
        break;
      case "last2Years":
        pastYearLimit = currentYear - 2;
        break;
      case "all":
        pastYearLimit = 0; // Include all years
        break;
      default:
        pastYearLimit = currentYear;
    }

    return historyData
      .filter((data) => data.year >= pastYearLimit)
      .sort((a, b) => new Date(a.year, a.month - 1) - new Date(b.year, b.month - 1));
  };

  const formatChartData = (data) => {
    const labels = data.map((item) => `${item.month}/${item.year}`);
    const values = data.map((item) => item.averageResult);

    return {
      labels,
      datasets: [
        {
          label: t("user.dashboards.performance"),
          data: values,
          borderColor: "blue",
          backgroundColor: "rgba(0, 123, 255, 0.5)",
          tension: 0.4,
        },
      ],
    };
  };

  if (loading) {
    return <LoadingScreen />;
  }

  const chartOptions = {
    responsive: true,
    scales: {
      y: {
        beginAtZero: true,
        suggestedMax: 100,
        ticks: {
          stepSize: 10,
        },
      },
    },
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5">{isMe ? "My Scorecard" : `${employee.name}'s Scorecard`}</Typography>
      <Box sx={{ width: "100%", maxWidth: 800, margin: "auto", textAlign: "center" }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {t("user.myScorecard.performanceHistoryChart")}
        </Typography>

        {/* Time Range Buttons */}
        <ButtonGroup variant="contained" sx={{ mb: 3 }}>
          <MDButton
            onClick={() => setSelectedView("thisYear")}
            color="primary"
            variant={selectedView === "thisYear" ? "contained" : "outlined"}
          >
            {t("user.myScorecard.thisYear")}
          </MDButton>

          <MDButton
            onClick={() => setSelectedView("lastYear")}
            color="primary"
            variant={selectedView === "lastYear" ? "contained" : "outlined"}
          >
            {t("user.myScorecard.lastYear")}
          </MDButton>

          <MDButton
            onClick={() => setSelectedView("last2Years")}
            color="primary"
            variant={selectedView === "last2Years" ? "contained" : "outlined"}
          >
            {t("user.myScorecard.last2Years")}
          </MDButton>

          <MDButton
            onClick={() => setSelectedView("all")}
            color="primary"
            variant={selectedView === "all" ? "contained" : "outlined"}
          >
            {t("user.myScorecard.allTime")}
          </MDButton>
        </ButtonGroup>

        {/* Line Chart */}
        {chartData ? <Line data={chartData} options={chartOptions} /> : null}
      </Box>
    </Box>
  );
};

WeighedHistoryChart.propTypes = {
  employee: PropTypes.object.isRequired,
  isMe: PropTypes.bool.isRequired,
};

export default WeighedHistoryChart;
